import { useState, useEffect, useRef } from "react";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Pen, Trash2, Loader2, CheckCircle2,
  User, AlertCircle, Image, Plus, X
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";

export default function SignatureManagement() {
  const navigate = useNavigate();
  const [feeEarners, setFeeEarners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [previewSig, setPreviewSig] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [addingMember, setAddingMember] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);

  const fetchSignatures = async () => {
    try {
      const res = await axios.get(`${API}/signatures`);
      setFeeEarners(res.data);
    } catch (err) {
      toast.error("Failed to load fee earners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSignatures(); }, []);

  const handleUploadClick = (email, name) => {
    setSelectedEmail(email);
    setSelectedName(name);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEmail) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG)");
      return;
    }
    if (file.size > 500000) {
      toast.error("File too large. Max 500KB.");
      return;
    }

    setUploading(selectedEmail);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("email", selectedEmail);
      formData.append("name", selectedName);

      await axios.post(`${API}/signatures/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Signature uploaded for ${selectedName}`);
      await fetchSignatures();
    } catch (err) {
      toast.error(errorMessage(err, "Upload failed"));
    } finally {
      setUploading(null);
      setSelectedEmail(null);
      setSelectedName(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (email, name) => {
    if (!window.confirm(`Remove signature for ${name}?`)) return;
    setDeleting(email);
    try {
      await axios.delete(`${API}/signatures/${email}`);
      toast.success(`Signature removed for ${name}`);
      await fetchSignatures();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to remove"));
    } finally {
      setDeleting(null);
    }
  };

  const handlePreview = async (email) => {
    try {
      const res = await axios.get(`${API}/signatures/${email}`);
      setPreviewSig(res.data);
    } catch {
      toast.error("Failed to load signature");
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim()) return toast.error("Name and email required");
    setAddingMember(true);
    try {
      const formData = new FormData();
      formData.append("name", newName.trim());
      formData.append("email", newEmail.trim().toLowerCase());
      await axios.post(`${API}/signatures/team/add`, formData);
      toast.success(`${newName} added`);
      setNewName(""); setNewEmail(""); setShowAddForm(false);
      await fetchSignatures();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to add member"));
    }
    setAddingMember(false);
  };

  const handleRemoveMember = async (email, name) => {
    if (!window.confirm(`Remove ${name} (${email}) from the team? This will also delete their signature.`)) return;
    setRemovingMember(email);
    try {
      await axios.delete(`${API}/signatures/team/${email}`);
      toast.success(`${name} removed`);
      await fetchSignatures();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to remove"));
    }
    setRemovingMember(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Fee Earner Signatures</h1>
            <p className="text-sm text-txt-muted">Upload signature images for CCL approvals</p>
          </div>
          <Button
            size="sm"
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-teal hover:bg-teal-hover text-white text-xs gap-1"
            data-testid="add-member-btn"
          >
            <Plus className="w-3 h-3" /> Add Team Member
          </Button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleFileChange}
          data-testid="signature-file-input"
        />

        {/* Add Team Member Form */}
        {showAddForm && (
          <form onSubmit={handleAddMember} className="glass-card p-4 flex flex-wrap items-end gap-3" data-testid="add-member-form">
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs text-txt-muted mb-1 block">Name</label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name" className="bg-navy-card border-navy-border text-white text-sm h-9" data-testid="new-member-name" required />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs text-txt-muted mb-1 block">Email</label>
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="name@bonsai.law" className="bg-navy-card border-navy-border text-white text-sm h-9" data-testid="new-member-email" required />
            </div>
            <Button type="submit" size="sm" disabled={addingMember} className="bg-teal hover:bg-teal-hover text-white h-9" data-testid="confirm-add-member">
              {addingMember ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddForm(false)} className="text-txt-muted h-9">
              <X className="w-3 h-3" />
            </Button>
          </form>
        )}

        {/* Fee Earner List */}
        <div className="space-y-3" data-testid="fee-earner-list">
          {feeEarners.map((fe) => (
            <div
              key={fe.email}
              className="glass-card p-4 flex items-center justify-between"
              data-testid={`fee-earner-row-${fe.email}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-teal/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-teal" />
                </div>
                <div>
                  <div className="font-medium text-white">{fe.name}</div>
                  <div className="text-xs text-txt-muted">{fe.email}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {fe.has_signature ? (
                  <>
                    <span className="text-xs bg-status-green-dim/30 text-status-green px-2 py-1 rounded-md flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Uploaded
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreview(fe.email)}
                      className="text-xs gap-1"
                      data-testid={`preview-sig-${fe.email}`}
                    >
                      <Image className="w-3 h-3" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUploadClick(fe.email, fe.name)}
                      disabled={uploading === fe.email}
                      className="text-xs gap-1"
                      data-testid={`replace-sig-${fe.email}`}
                    >
                      {uploading === fe.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      Replace
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(fe.email, fe.name)}
                      disabled={deleting === fe.email}
                      className="text-xs text-status-red hover:text-status-red gap-1"
                      data-testid={`delete-sig-${fe.email}`}
                    >
                      {deleting === fe.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleUploadClick(fe.email, fe.name)}
                    disabled={uploading === fe.email}
                    className="bg-teal hover:bg-teal-hover text-white text-xs gap-1"
                    data-testid={`upload-sig-${fe.email}`}
                  >
                    {uploading === fe.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pen className="w-3 h-3" />}
                    Upload Signature
                  </Button>
                )}
                {/* Remove team member (directors) */}
                {fe.role !== "director" && fe.email !== "vanessa@bonsai.law" && fe.email !== "rebekah@bonsai.law" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveMember(fe.email, fe.name)}
                    disabled={removingMember === fe.email}
                    className="text-xs text-status-red/50 hover:text-status-red gap-1 ml-1"
                    data-testid={`remove-member-${fe.email}`}
                  >
                    {removingMember === fe.email ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {feeEarners.length === 0 && (
            <div className="glass-card p-8 text-center">
              <AlertCircle className="w-8 h-8 text-txt-muted mx-auto mb-3" />
              <p className="text-txt-muted">No fee earners found. Add users to CaseMinder first.</p>
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {previewSig && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewSig(null)}>
            <div className="bg-white rounded-md p-6 max-w-md w-full mx-4 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="signature-preview-modal">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-txt-faint">{previewSig.name}'s Signature</h3>
                <button onClick={() => setPreviewSig(null)} className="text-gray-400 hover:text-gray-600">&times;</button>
              </div>
              <div className="border-2 border-dashed border-gray-200 rounded-md p-4 bg-white flex items-center justify-center min-h-[120px]">
                <img
                  src={previewSig.signature_data}
                  alt={`${previewSig.name} signature`}
                  className="max-h-[100px] max-w-full object-contain"
                  data-testid="signature-preview-image"
                />
              </div>
              <div className="text-xs text-gray-500 text-center">
                Uploaded: {new Date(previewSig.uploaded_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
